import type { ReactElement } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { weekAndMonthDay } from 'utils/dateTimes';
import useCurrentUser from 'hooks/useCurrentUser';

import SkeletonLine from 'components/shared/skeletons/SkeletonLine';
import AccountSettingsModal from '../modals/AccountSettingsModal';

type Props = {
  className?: string;
};

function AccountSettingsCard({ className }: Props): ReactElement {
  const { currentUser } = useCurrentUser();

  const {
    email,
    subscriptionStatus,
    isSuspended,
    isEmpty,
    subscription,
    isReturning,
  } = currentUser;

  const { next_scheduled_order_date: nextScheduledOrderDate } = subscription || {};
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  return (
    <div className={`c-card c-profile__card col-12 col-sm-6 col-lg-4 ${className}`}>
      <div className="c-card__content">
        <h2 className="c-profile__card-heading">
          Account Settings
          <button
            type="button"
            onClick={() => setShowSettingsModal(true)}
            className="c-link u-text--sm"
            aria-label="Edit account settings"
          >
            Edit
          </button>
        </h2>
        <h3 className="c-profile__label">Email</h3>

        <span>
          {isEmpty ? (
            <SkeletonLine width={180} height={14} />
          ) : (
            email
          )}
        </span>

        <h3 className="c-profile__label">Password</h3>
        <span>
          {isEmpty ? (
            <SkeletonLine width={80} height={14} />
          ) : (
            'xxxxxxxx'
          )}
        </span>

        <h3 className="c-profile__label">Subscription Status</h3>

        <div
          className={[
            'c-profile__status',
            isSuspended ? 'is-invalid' : '',
          ].join(' ')}
        >
          {isEmpty ? (
            <SkeletonLine width={80} height={14} />
          ) : (
            <>
              <span>
                {subscriptionStatus}
                {isReturning ? '.' : ''}
              </span>
              &nbsp;
              <span>
                {isReturning
                  && `Next delivery is scheduled for
                ${weekAndMonthDay(nextScheduledOrderDate)} `}
              </span>
              {subscriptionStatus !== 'Cancelled' && (
                <Link
                  to="/manage-subscription"
                  aria-label="Change subscription status"
                  className="u-text--sm"
                >
                  Update subscription
                </Link>
              )}
            </>
          )}
        </div>
      </div>

      {showSettingsModal && (
        <AccountSettingsModal
          show={showSettingsModal}
          onClose={() => setShowSettingsModal(false)}
        />
      )}
    </div>
  );
}

export default AccountSettingsCard;
